import Papa from 'papaparse';
import axios from 'axios';
import Config from '../../../config';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const FileSaver = require('filesaver.js-npm');

export interface UserImportRecord {
  email: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  permissions?: string[];
}

export interface UserUpdateRecord extends UserImportRecord {
  userId: string;
  currentValues: Partial<UserImportRecord>;
}

export interface SkippedRecord {
  email: string;
  reason: string;
}

export interface ImportError {
  row: number;
  email?: string;
  message: string;
}

export interface UserImportData {
  toInvite: UserImportRecord[];
  toUpdate: UserUpdateRecord[];
  skipped: SkippedRecord[];
  errors: ImportError[];
}

export interface ImportResults {
  success: string[];
  errors: string[];
}

export interface ExistingUser {
  Id: string;
  UserName: string;
  Email: string;
  FirstName: string;
  LastName: string;
  Title: string;
  Permissions: string[];
}

const TEMPLATE_HEADERS = 'Email,First Name,Last Name,Title,Permissions';
const TEMPLATE_EXAMPLE = 'user@company.com,Jane,Smith,Onboarding Manager,users/manage; orders/manage';

export const UserImportService = {
  generateTemplate(): void {
    const content = `${TEMPLATE_HEADERS}\n${TEMPLATE_EXAMPLE}`;
    const blob = new Blob([content], { type: 'text/csv' });
    FileSaver.saveAs(blob, 'users_import_template.csv');
  },

  validateAndCategorize(
    records: Record<string, string>[],
    existingUsers: ExistingUser[],
    allPermissions: string[]
  ): UserImportData {
    const errors: ImportError[] = [];
    const toInvite: UserImportRecord[] = [];
    const toUpdate: UserUpdateRecord[] = [];
    const skipped: SkippedRecord[] = [];
    const seenEmails = new Set<string>();

    for (let i = 0; i < records.length; i++) {
      const record = records[i];
      const email = (record['Email'] || '').trim();
      const firstName = (record['First Name'] || '').trim();
      const lastName = (record['Last Name'] || '').trim();
      const title = (record['Title'] || '').trim();
      const permissionsRaw = (record['Permissions'] || '').trim();
      const rowNum = i + 2; // 1-indexed + header row

      // Validate email
      if (!email) {
        errors.push({ row: rowNum, message: 'Email is required' });
        continue;
      }

      const emailLower = email.toLowerCase();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailLower)) {
        errors.push({ row: rowNum, email, message: `Invalid email format: ${email}` });
        continue;
      }

      // Check for duplicates within the CSV
      if (seenEmails.has(emailLower)) {
        errors.push({ row: rowNum, email, message: `Duplicate email in file: ${email}` });
        continue;
      }
      seenEmails.add(emailLower);

      // Parse permissions
      let permissions: string[] | undefined;
      if (permissionsRaw) {
        permissions = permissionsRaw.split(/[;|]/).map(p => p.trim()).filter(p => p);
        const invalidPerms = permissions.filter(p => !allPermissions.includes(p));
        if (invalidPerms.length > 0) {
          errors.push({ row: rowNum, email, message: `Invalid permission(s): ${invalidPerms.join(', ')}` });
          continue;
        }
      }

      // Find existing user by email (case-insensitive, trimmed)
      const existingUser = existingUsers.find(u => (u.Email || '').trim().toLowerCase() === emailLower);

      if (existingUser) {
        // Determine what changed — only consider non-blank CSV fields
        const changes: Partial<UserImportRecord> = {};
        if (firstName && firstName !== existingUser.FirstName) changes.firstName = firstName;
        if (lastName && lastName !== existingUser.LastName) changes.lastName = lastName;
        if (title && title !== existingUser.Title) changes.title = title;

        if (permissions) {
          const existingPerms = [...(existingUser.Permissions || [])].sort();
          const newPerms = [...permissions].sort();
          if (JSON.stringify(existingPerms) !== JSON.stringify(newPerms)) {
            changes.permissions = permissions;
          }
        }

        if (Object.keys(changes).length > 0) {
          toUpdate.push({
            email: existingUser.Email,
            userId: existingUser.Id,
            firstName: firstName || undefined,
            lastName: lastName || undefined,
            title: title || undefined,
            permissions,
            currentValues: {
              firstName: existingUser.FirstName,
              lastName: existingUser.LastName,
              title: existingUser.Title,
              permissions: existingUser.Permissions,
            },
            ...changes,
          });
        } else {
          skipped.push({ email: existingUser.Email, reason: 'No changes detected' });
        }
      } else {
        // New user — will be invited
        toInvite.push({
          email,
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          title: title || undefined,
          permissions: permissions || undefined,
        });
      }
    }

    return { toInvite, toUpdate, skipped, errors };
  },

  parseCSV(file: File): Promise<Record<string, string>[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result;
          if (typeof text !== 'string') {
            reject(new Error('Failed to read file as text'));
            return;
          }
          const cleanText = text.replace(/^\uFEFF/, '');
          const result = Papa.parse(cleanText, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (h: string) => h.trim(),
            transform: (v: string) => v.replace(/^\s+|\s+$/g, ''),
          });
          resolve(result.data as Record<string, string>[]);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  },

  async processImport(
    importData: UserImportData,
    onProgress: (percent: number) => void
  ): Promise<ImportResults> {
    const BATCH_SIZE = 5;
    const results: ImportResults = { success: [], errors: [] };

    // Combine all operations into a single list
    const operations: Array<{ type: 'invite' | 'update'; record: UserImportRecord | UserUpdateRecord }> = [];
    importData.toInvite.forEach(r => operations.push({ type: 'invite', record: r }));
    importData.toUpdate.forEach(r => operations.push({ type: 'update', record: r }));

    let completed = 0;
    const total = operations.length;

    if (total === 0) {
      onProgress(100);
      return results;
    }

    for (let i = 0; i < operations.length; i += BATCH_SIZE) {
      const batch = operations.slice(i, i + BATCH_SIZE);
      await Promise.allSettled(
        batch.map(async (op) => {
          if (op.type === 'invite') {
            const rec = op.record as UserImportRecord;
            try {
              await axios.post(Config.oauth + '/user/invite', {
                Email: rec.email,
                Permissions: rec.permissions || [],
              });
              results.success.push(`Invited: ${rec.email}`);
            } catch (error: any) {
              results.errors.push(`Failed to invite ${rec.email}: ${error.message}`);
            }
          } else {
            const rec = op.record as UserUpdateRecord;
            try {
              const payload: Record<string, any> = { Id: rec.userId };
              if (rec.firstName) payload.FirstName = rec.firstName;
              if (rec.lastName) payload.LastName = rec.lastName;
              if (rec.title) payload.Title = rec.title;
              if (rec.permissions) payload.Permissions = rec.permissions;

              const res = (await axios.put(Config.oauth + '/user/userprofile', payload)).data;
              if (res?.Errors && Object.keys(res.Errors).length > 0) {
                const errMsgs = Object.keys(res.Errors)
                  .map(k => res.Errors[k].errors.join(', '))
                  .join('; ');
                results.errors.push(`Failed to update ${rec.email}: ${errMsgs}`);
              } else {
                results.success.push(`Changed: ${rec.email}`);
              }
            } catch (error: any) {
              results.errors.push(`Failed to update ${rec.email}: ${error.message}`);
            }
          }
        })
      );
      completed += batch.length;
      onProgress(Math.round((completed / total) * 100));
    }

    return results;
  },
};
